import { Injectable, Injector, Renderer2, RendererFactory2 } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
import { AlertController, ModalController } from '@ionic/angular';
import { TranslocoService } from '@jsverse/transloco';
import { ApiAuthenticationService } from '@timechef/core/api';
import { AuthRepository } from '@timechef/core/auth';
import { UserSite } from '@timechef/core/auth/auth.models';
import { I18nService } from '@timechef/core/i18n/i18n.service';
import { ILogger, LoggerService } from '@timechef/core/logging';
import { dismissFactory } from '@timechef/core/utils/alert-utils';
import { RegistrationRepository } from '@timechef/features/registration/state/registration.repository';
import { catchError, of, tap, throwError } from 'rxjs';
import { OnboardingModalComponent } from '../shared/components/onboarding-modal/onboarding-modal.component';
import { AccountEmailModalComponent } from '../shared/containers/account-email-modal/account-email-modal.component';
import { AccountProfileModalComponent } from '../shared/containers/account-profile-modal/account-profile-modal.component';
import { AccountQrCodeModalComponent } from '../shared/containers/account-qr-code-modal/account-qr-code-modal/account-qr-code-modal.component';
import { ChangePasswordModalComponent } from '../shared/containers/change-password-modal/change-password-modal.component';
import { LikesFilterModalComponent } from '../shared/containers/likes-filter-modal/likes-filter-modal.component';
import { ONBOARDING_SLIDES } from './account.data';
import { LikesFilterEnum, OnboardingInfos } from './account.models';

import { GoogleAnalyticsService } from '@timechef/core/analytics';
import { environment } from 'src/environments/environment';
import { ParametersModalComponent } from '../shared/containers/parameters-modal/parameters-modal.component';
import { AccountRepository, AccountStatusKeys, buildUpdateDishLikeNotificationKey } from './account.repository';
@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private renderer: Renderer2;
  private authRepo: AuthRepository;
  private logger: ILogger;

  constructor(
    private apiAuth: ApiAuthenticationService,
    private accountRepo: AccountRepository,
    private registrationRepo: RegistrationRepository,
    private alertCtrl: AlertController,
    private i18n: I18nService,
    injector: Injector,
    renderer: RendererFactory2,
    private translocoService: TranslocoService,
    private modalCtrl: ModalController,
    private googleAnalyticsService: GoogleAnalyticsService,
  ) {
    this.authRepo = injector.get(AuthRepository);
    this.renderer = renderer.createRenderer(document.body, null);
    this.logger = injector.get(LoggerService).getLogger('AccountService');
  }

  changeLanguage(language: string, cfg: { skipApiCall: boolean } = { skipApiCall: false }) {
    this.translocoService.setActiveLang(language);
    this.accountRepo.changeLanguage(language);

    if (!cfg.skipApiCall)
      this.apiAuth
        .putLanguage({ body: { language } })
        .pipe(
          catchError((err) => {
            this.logger.error('Could not update silently preferred language', err);
            return throwError(() => err);
          }),
        )
        .subscribe(() => {
          this.logger.info('Silent update of preferred language done');
        });
  }

  setTheme(theme: 'light' | 'dark') {
    this.accountRepo.setTheme(theme);

    const isDark = theme === 'dark';
    setStatusBar(isDark);

    if (isDark) this.renderer.addClass(document.body, 'dark');
    else this.renderer.removeClass(document.body, 'dark');
  }

  updateDishLikeNotification(dishId: number, isNotificationEnabled: boolean) {
    const request = this.accountRepo.getUpdateDishLikeNotificationRequestStatus(dishId);
    if (request && request.value === 'pending') {
      return of();
    }

    return this.apiAuth.patchLikesByDishidUpdateNotification({ DishId: dishId, body: { isNotificationEnabled } }).pipe(
      this.accountRepo.trackRequestStatus(AccountStatusKeys.UpdateDishLikeNotification),
      this.accountRepo.trackRequestStatus(buildUpdateDishLikeNotificationKey(dishId)),
      tap(() => {
        this.accountRepo.setDishLikeNotification(dishId, isNotificationEnabled);
      }),
    );
  }

  async openParametersModal() {
    const modal = await this.modalCtrl.create({
      component: ParametersModalComponent,
    });

    await modal.present();
    await this.googleAnalyticsService.trackPageView('/account/parameters', 'Parameters');
  }

  async openAccountProfileModal(setFocusToPhoneInput?: boolean) {
    this.registrationRepo.resetUnregisterStatus();
    this.authRepo.resetUpdateAccountInfosStatus();

    const dimiss = dismissFactory(this.alertCtrl, {
      header: this.i18n.translate(getAccountInfosModalDismissKey('HEADER')),
      message: this.i18n.translate(getAccountInfosModalDismissKey('MESSAGE')),
      buttons: [
        this.i18n.translate(getAccountInfosModalDismissKey('NO')),
        { text: this.i18n.translate(getAccountInfosModalDismissKey('YES')), role: 'confirm' },
      ],
    });

    const modal = await this.modalCtrl.create({
      component: AccountProfileModalComponent,
      componentProps: {
        updateHasChanges: dimiss.updateHasChanges,
        setFocusToPhoneInput,
      },
      canDismiss: dimiss.canDismiss,
    });

    await modal.present();
    await this.googleAnalyticsService.trackPageView('/account/profile', 'Profile');
  }

  async openAccountEmailModal() {
    const dimiss = dismissFactory(this.alertCtrl, {
      header: this.i18n.translate(getAccountEmailModalDismissKey('HEADER')),
      message: this.i18n.translate(getAccountEmailModalDismissKey('MESSAGE')),
      buttons: [
        this.i18n.translate(getAccountEmailModalDismissKey('NO')),
        { text: this.i18n.translate(getAccountEmailModalDismissKey('YES')), role: 'confirm' },
      ],
    });

    const modal = await this.modalCtrl.create({
      component: AccountEmailModalComponent,
      componentProps: {
        updateHasChanges: dimiss.updateHasChanges,
      },
      canDismiss: dimiss.canDismiss,
    });

    await modal.present();
    await this.googleAnalyticsService.trackPageView('/account/change-email', 'Account | Change email');
  }

  async openChangePasswordModal() {
    const dimiss = dismissFactory(this.alertCtrl, {
      header: this.i18n.translate(getChangePasswordModalDismissKey('HEADER')),
      message: this.i18n.translate(getChangePasswordModalDismissKey('MESSAGE')),
      buttons: [
        this.i18n.translate(getChangePasswordModalDismissKey('NO')),
        { text: this.i18n.translate(getChangePasswordModalDismissKey('YES')), role: 'confirm' },
      ],
    });

    const modal = await this.modalCtrl.create({
      component: ChangePasswordModalComponent,
      componentProps: {
        updateHasChanges: dimiss.updateHasChanges,
      },
      canDismiss: dimiss.canDismiss,
    });
    await modal.present();
    await this.googleAnalyticsService.trackPageView('/account/change-password', 'Account | Change password');
  }

  async openOnboardingModal() {
    const onboardingInfos = this.accountRepo.getOnboardingInfos();

    const modal = await this.modalCtrl.create({
      component: OnboardingModalComponent,
      componentProps: { onboardingInfos },
    });

    await modal.present();
    await this.googleAnalyticsService.trackPageView('/onboarding', 'Onboarding');

    await modal.onDidDismiss();
    if (onboardingInfos) {
      const onboardingScreensKeys: string[] = onboardingInfos.map((e) => e.key);
      this.accountRepo.setOnboardingScreensAsSeen(onboardingScreensKeys);
    }
  }

  async openQrCodeModal(qrCodeInfo: string) {
    const modal = await this.modalCtrl.create({
      component: AccountQrCodeModalComponent,
      componentProps: {
        qrCodeInfo,
      },
    });
    await modal.present();
    await this.googleAnalyticsService.trackPageView('/account/qrcode', 'Account | QRCode');
  }

  async promptOnboardingIfRequired() {
    if (!environment.enableOnboarding) {
      this.logger.debug('Onboarding disabled in config, do not prompt');
      return;
    }

    if (this.accountRepo.getHasOnboardingInfosToDisplay()) this.openOnboardingModal();
  }

  getLikes() {
    return this.apiAuth.getUserLikes().pipe(
      this.accountRepo.trackRequestStatus(AccountStatusKeys.Likes),
      tap((x) => {
        this.accountRepo.setLikeDishFamilies(x.dishFamilies);
      }),
    );
  }

  updateOnboarding(
    site: UserSite,
    restaurants: { allowOrder: boolean; allowOrderHistory: boolean; hasMenu: boolean; hasFluidite: boolean; hasAntiGaspi: boolean },
  ) {
    if (!environment.enableOnboarding) {
      this.logger.debug('updateOnboarding requested but Onboarding is disabled, do not update');
      return;
    }

    const slides: OnboardingInfos[] = [];

    //Reload
    if (site.isCashRegisterUsable) {
      slides.push(ONBOARDING_SLIDES.Reload);
    }

    //Fluidite
    if (restaurants.hasFluidite) {
      slides.push(ONBOARDING_SLIDES.Fluidite);
    }

    //Nutriscore - Favorite
    if (restaurants.hasMenu) {
      slides.push(ONBOARDING_SLIDES.Nutriscore);
      slides.push(ONBOARDING_SLIDES.Favorite);
    }

    //GreenScore
    if (site.showGreenscore) {
      slides.push(ONBOARDING_SLIDES.Greenscore);
    }

    //Booking
    if (site.showBooking) {
      slides.push(ONBOARDING_SLIDES.Booking);
    }

    //Order
    if (restaurants.allowOrder) {
      slides.push(ONBOARDING_SLIDES.Order);
    }

    //AntiGaspi
    if (restaurants.hasAntiGaspi) {
      slides.push(ONBOARDING_SLIDES.AntiGaspi);
    }
    this.accountRepo.updateOnboarding(slides);
  }

  async openLikesFilterModal() {
    const filter = this.accountRepo.getLikesFilter();
    const modal = await this.modalCtrl.create({
      component: LikesFilterModalComponent,
      componentProps: {
        filter,
      },
      breakpoints: [0, 1],
      initialBreakpoint: 1,
    });
    await modal.present();
    await this.googleAnalyticsService.trackPageView('/account/favorites/filter', 'Account | Favorites | Filter');

    const modalResult = await modal.onDidDismiss<LikesFilterEnum>();
    if (modalResult?.data) {
      this.accountRepo.setLikesFilter(modalResult.data);
    }

    return true;
  }

  updateStatusBar() {
    const theme = this.accountRepo.getTheme();
    setStatusBar(theme === 'dark' ? true : false);
  }
}

export const setStatusBar = (isDark: boolean, isFirstLaunch?: boolean) => {
  if (Capacitor.isNativePlatform()) {
    setTimeout(async () => {
      await EdgeToEdge.setBackgroundColor({ color: isDark ? '#000000' : '#ffffff' });
      StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
      StatusBar.setBackgroundColor({ color: isDark ? '#000000' : '#ffffff' });
    }, 150);
    if (isFirstLaunch) {
      // Temporary fix for status bar item color not updating on first launch
      setTimeout(async () => {
        StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
      }, 700);
    }
  }
};

const getAccountEmailModalDismissKey = (key: string) => `ACCOUNT_INFOS_MODAL_COMPONENT.BACK_ALERT.${key}`;
const getAccountInfosModalDismissKey = (key: string) => `ACCOUNT_INFOS_MODAL_COMPONENT.BACK_ALERT.${key}`;

const getChangePasswordModalDismissKey = (key: string) => `ACCOUNT_CHANGE_PASSWORD_MODAL_COMPONENT.BACK_ALERT.${key}`;
